export type Provider = 'openai' | 'anthropic' | 'gemini' | 'openrouter' | 'grok';
export type ChannelStatus = 'healthy' | 'degraded' | 'failed' | 'cooldown';
export type DispatchHealthGrade = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
export type ErrorKind =
  | 'auth_error'
  | 'quota_exhausted'
  | 'rate_limited'
  | 'overloaded'
  | 'timeout'
  | 'network_error';

export interface GroupSummary {
  id: string;
  name: string;
  mode: 'smart' | 'forced';
  forcedChannelId?: string;
  probeIntervalSec: number;
  maxAcceptableTtftMs: number;
  dispatchStrategy?: DispatchStrategy;
  includeNonStreamUsage?: boolean;
  officialUsdToday?: number;
  spendCnyToday?: number;
  compositeCostToday?: number;
  officialUsd24h: number;
  spendCny24h: number;
  compositeCost24h: number;
}

export type DispatchStrategy = 'stable_first' | 'cost_first' | 'balanced';
export type ChannelSortMode = 'recommended' | 'abnormal_first' | 'cost_asc' | 'cost_desc' | 'health_desc' | 'ttft_asc' | 'score_desc';
export type OAuthPreferenceMode = 'off' | 'healthy_tiebreak' | 'prefer_healthy';

export interface TimelinePoint {
  status: ChannelStatus | 'idle';
  ttftMs: number | null;
  pingMs: number | null;
  checkedAt: string;
}

export interface ScorePart {
  label: string;
  value: number;
  max: number;
  tone: 'good' | 'warn' | 'bad' | 'neutral';
}

export interface DispatchScore {
  total: number;
  components: {
    stability: number;
    latency: number;
    cacheAffinity: number;
    nativePriority: number;
    manualPriority: number;
    costPenalty: number;
    switchingPenalty: number;
    inflightPenalty: number;
    healthPenalty: number;
  };
}

export interface ScoreBreakdown {
  AccountID: string;
  accountName?: string;
  costCnyPerUsd?: number;
  Total: number;
  Components: {
    Stability: number;
    Latency: number;
    CacheAffinity: number;
    NativePriority: number;
    ManualPriority: number;
    CostPenalty: number;
    SwitchingPenalty: number;
    InflightPenalty: number;
    HealthPenalty: number;
  };
}

export interface RecentDecision {
  requestId: string;
  groupId: string;
  platform: string;
  model: string;
  decision: string;
  reason: string;
  selectedAccountId?: string;
  scores: ScoreBreakdown[];
  skippedByReason: Record<string, number>;
  createdAt: string;
  groupName?: string;
  reasonText?: string;
  selectedAccountName?: string;
  previousAccountId?: string;
  previousAccountName?: string;
  selectedCostCnyPerUsd?: number;
  previousCostCnyPerUsd?: number;
  costDeltaCnyPerUsd?: number;
  changeType?: 'initial' | 'same' | 'switch' | string;
  selectedScore?: number;
  previousScore?: number;
}

export interface DispatchReasonCount {
  reason: string;
  reasonText: string;
  count: number;
}

export interface DispatchRecordSummary {
  totalDecisions: number;
  switches: number;
  costDownSwitches: number;
  costUpSwitches: number;
  noChange: number;
  averageSelectedCost: number;
  estimatedCostDelta: number;
  topReasons: DispatchReasonCount[];
}

export interface DispatchRecordReport {
  groupId: string;
  rangeHours: number;
  generatedAt: string;
  summary: DispatchRecordSummary;
  records: RecentDecision[];
}

export interface HealthWindow {
  window: string;
  requests: number;
  failures?: number;
  successRate: number;
  slowRate: number;
  severeSlowCount: number;
}

export interface TrendPoint {
  label: string;
  success: number;
  failed: number;
}

export interface ProbeLog {
  time: string;
  status: ChannelStatus;
  model: string;
  latencyMs: number | null;
  message: string;
}

export interface ErrorDetail {
  statusCode: number;
  errorType: string;
  errorMessage: string;
  count30m: number;
  lastSeenAt: string;
}

export interface Channel {
  id: string;
  name: string;
  provider: Provider;
  providerLabel: string;
  model: string;
  probeModel: string;
  probePrompt: string;
  probeAsClaudeCode: boolean;
  availableModels?: string[];
  group: GroupSummary;
  enabled: boolean;
  schedulable?: boolean;
  accountStatus?: string;
  status: ChannelStatus;
  statusText: string;
  dispatchHealthGrade: DispatchHealthGrade;
  dispatchHealthText: string;
  dispatchHealthReasons: string[];
  healthDataAvailable?: boolean;
  healthDataUpdatedAt?: string;
  ttftMs: number | null;
  pingMs: number | null;
  availability7d: number;
  costCnyPerUsd: number;
  manualPriority: number;
  forced: boolean;
  requestToday?: number;
  officialUsdToday?: number;
  availabilityToday?: number;
  compositeCostToday?: number;
  request24h: number;
  officialUsd24h: number;
  cacheTokens24h: number;
  compositeCost24h: number;
  score: number;
  scoreParts: ScorePart[];
  dispatchScore?: DispatchScore;
  healthWindows: HealthWindow[];
  isDispatching?: boolean;
  errorTrend: Record<ErrorKind, number>;
  errorDetails: ErrorDetail[];
  requestTrend: TrendPoint[];
  probeLogs: ProbeLog[];
  timeline: TimelinePoint[];
  usageTimeline: TimelinePoint[];
  probeTimeline: TimelinePoint[];
  timelinesLoading?: boolean;
  timelinesError?: boolean;
}

export interface DashboardData {
  groups: GroupSummary[];
  providers: Provider[];
  channels: Channel[];
  runtime: RuntimeConfig;
  healthDataStale?: boolean;
  recentDecisions: RecentDecision[];
  updatedAt: string;
}

export interface DashboardChannelTimeline {
  accountId: string;
  loaded: boolean;
  ttftMs: number | null;
  pingMs: number | null;
  probeLogs: ProbeLog[];
  timeline: TimelinePoint[];
  usageTimeline: TimelinePoint[];
  probeTimeline: TimelinePoint[];
}

export interface DashboardGroupTimelines {
  groupId: string;
  channels: DashboardChannelTimeline[];
  updatedAt: string;
}

export interface RuntimeConfig {
  dispatchEnabled: boolean;
  dispatchOAuthPreference: OAuthPreferenceMode;
  dispatchFailOpen: boolean;
  dispatchSelectTimeoutMs: number;
  dispatchAutoBypassFailures: number;
  dispatchAutoRecover: boolean;
  probeEnabled: boolean;
  probeIntervalSeconds: number;
  probeImageIntervalSeconds: number;
  probeJitterSeconds: number;
  probeMaxConcurrentAccounts: number;
  probeTimeoutSeconds: number;
  probeRetryCount: number;
  alertMergeWindowMinutes: number;
  dataRetentionDays: number;
}

export interface BackgroundTaskStatus {
  task: string;
  label: string;
  lastRunAt: number;
  success: boolean;
  message?: string;
}

export interface StorageUsage {
  category: string;
  label: string;
  bytes: number;
  rows: number;
}

export interface SystemDiagnostics {
  lastBalanceSyncAt: number;
  lastCostSyncAt: number;
  lastProbeAt: number;
  latestFailure?: string;
  tasks: BackgroundTaskStatus[];
  storage: StorageUsage[];
}

export type ChannelBalanceAccountType = 'newapi' | 'sub2api' | 'futureapi';
export type ContactChannel = '' | 'wechat' | 'qq' | 'telegram';

export interface ChannelBalanceAccount {
  id: number;
  name: string;
  type: ChannelBalanceAccountType;
  base_url: string;
  recharge_url: string;
  upstream_user_id: number;
  enabled: boolean;
  balance: number;
  balance_exchange_rate: number;
  converted_balance: number;
  unit: string;
  balance_updated_time: number;
  threshold: number;
  alert_enabled: boolean;
  alert_cooldown_hours: number;
  last_alert_time: number;
  contact_channel: ContactChannel;
  contact_info: string;
  remark: string;
  billing_identity?: string;
  sub2api_email?: string;
  recharge_multiplier: number;
  cost_sync_enabled: boolean;
  cost_sync_locked: boolean;
  cost_sync_last_time: number;
  cost_sync_last_success: boolean;
  cost_sync_last_message: string;
  created_at: string;
  updated_at: string;
  has_key: boolean;
  has_sub2api_password: boolean;
  low_balance: boolean;
}

export interface ChannelBalanceSummary {
  total: number;
  monitored: number;
  low_balance: number;
  alert_enabled: number;
  last_refresh_time: number;
}

export interface ChannelBalanceSettings {
  auto_refresh_minutes: number;
  cost_sync_interval_minutes: number;
}

export interface ChannelBalanceOverview {
  summary: ChannelBalanceSummary;
  settings: ChannelBalanceSettings;
  items: ChannelBalanceAccount[];
}

export interface ChannelBalanceAccountPayload {
  name: string;
  type: ChannelBalanceAccountType;
  base_url: string;
  recharge_url?: string;
  key?: string;
  sub2api_email?: string;
  sub2api_password?: string;
  recharge_multiplier?: number;
  balance_exchange_rate?: number;
  cost_sync_enabled?: boolean;
  cost_sync_locked?: boolean;
  upstream_user_id?: number;
  enabled?: boolean;
  threshold?: number;
  alert_enabled?: boolean;
  alert_cooldown_hours?: number;
  contact_channel?: ContactChannel;
  contact_info?: string;
  remark?: string;
  billing_identity?: string;
}

export interface ChannelBalanceRefreshResult {
  id: number;
  success: boolean;
  message?: string;
  balance?: number;
  converted_balance?: number;
  unit?: string;
  low_balance_alert_sent?: boolean;
  alert_message?: string;
}

export interface UpstreamCostSyncSettings {
  interval_minutes: number;
}

export interface UpstreamCostSyncResult {
  balance_account_id: number;
  balance_account_name?: string;
  account_id?: string;
  account_name?: string;
  api_key_fingerprint?: string;
  base_url?: string;
  upstream_type?: string;
  success: boolean;
  message?: string;
  group_id?: string;
  group_name?: string;
  group_multiplier?: number;
  normal_multiplier?: number;
  dedicated_multiplier?: number;
  multiplier_source?: string;
  recharge_multiplier?: number;
  old_cost?: number;
  new_cost?: number;
  updated: boolean;
  locked?: boolean;
}

export interface CostManagementSettings {
  interval_minutes: number;
  alert_enabled: boolean;
  alert_threshold_percent: number;
}

export interface CostManagementAccount {
  balance_account_id: number;
  balance_account_name: string;
  upstream_type: string;
  base_url: string;
  account_id: string;
  account_name: string;
  api_key_fingerprint: string;
  group_id: string;
  group_name: string;
  normal_multiplier?: number;
  dedicated_multiplier?: number;
  effective_multiplier: number;
  multiplier_source: string;
  recharge_multiplier: number;
  calculated_cost: number;
  previous_cost: number;
  current_config_cost: number;
  change_percent: number;
  status: string;
  message: string;
  locked: boolean;
  last_sync_time: number;
}

export interface CostManagementUpstream {
  balance_account_id: number;
  balance_account_name: string;
  upstream_type: string;
  base_url: string;
  recharge_multiplier: number;
  accounts: CostManagementAccount[];
}

export interface CostManagementHistory extends CostManagementAccount {
  id?: number;
  success: boolean;
  notified: boolean;
  sync_time: number;
  created_at: string;
}

export interface CostManagementOverview {
  settings: CostManagementSettings;
  upstreams: CostManagementUpstream[];
  recent_changes: CostManagementHistory[];
  updated_at: string;
}

export interface DailyProfitSource {
  name: string;
  status: string;
  message?: string;
  amount: number;
  requests: number;
  tokens: number;
}

export interface DailyProfitSummary {
  income: number;
  newapi_income: number;
  sub2api_direct_income: number;
  upstream_cost: number;
  profit: number;
  profit_rate: number;
  income_requests: number;
  upstream_requests: number;
  income_tokens: number;
  upstream_tokens: number;
  successful_cost_sources: number;
  failed_cost_sources: number;
}

export interface DailyProfitUpstreamCost {
  balance_account_id: number;
  balance_account_name: string;
  upstream_type: string;
  base_url: string;
  status: 'ok' | 'unsupported' | 'duplicate' | 'error' | string;
  message?: string;
  recharge_multiplier: number;
  actual_cost: number;
  standard_cost: number;
  converted_cost: number;
  requests: number;
  tokens: number;
}

export interface DailyProfitTrendPoint {
  date: string;
  income: number;
  upstream_cost: number;
  profit: number;
  profit_rate: number;
  complete: boolean;
}

export interface DailyProfitTrend {
  date: string;
  timezone: string;
  days: number;
  points: DailyProfitTrendPoint[];
  updated_at: string;
}

export interface DailyProfitOverview {
  date: string;
  timezone: string;
  start_at: string;
  end_at: string;
  complete: boolean;
  summary: DailyProfitSummary;
  income: {
    newapi: DailyProfitSource;
    sub2api_direct: DailyProfitSource;
  };
  upstream_costs: DailyProfitUpstreamCost[];
  warnings: string[] | null;
  updated_at: string;
}

export interface NotificationConfig {
  bark_enabled: boolean;
  bark_server_url: string;
  bark_device_key: string;
  bark_group: string;
  dingtalk_enabled: boolean;
  dingtalk_webhook: string;
  dingtalk_secret: string;
  email_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_tls_mode: 'starttls' | 'tls' | 'none';
  smtp_username: string;
  smtp_password: string;
  email_from: string;
  email_recipients: string;
  last_test_time: number;
  last_test_succeeded: boolean;
  last_test_message: string;
}

export interface NotificationTestResult {
  success: boolean;
  message: string;
}

export interface ChannelFilters {
  groupId: string;
  provider: Provider | 'all';
  search: string;
}

const API_ENDPOINT = '/api/admin/dashboard';

export async function fetchDashboard(options: { signal?: AbortSignal; useMock?: boolean } = {}): Promise<DashboardData> {
  if (options.useMock || mockFallbackEnabled()) return mockDashboard;

  try {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = adminToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(API_ENDPOINT, {
      headers,
      signal: options.signal,
    });
    if (!response.ok) throw new Error(response.status === 401 ? 'unauthorized' : `dashboard ${response.status}`);
    return (await response.json()) as DashboardData;
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw error;
  }
}

export async function fetchDashboardGroupTimelines(groupId: string, options: { signal?: AbortSignal; useMock?: boolean } = {}): Promise<DashboardGroupTimelines> {
  if (options.useMock || mockFallbackEnabled()) {
    return {
      groupId,
      channels: mockDashboard.channels
        .filter((channel) => channel.group.id === groupId)
        .map((channel) => ({
          accountId: channel.id,
          loaded: true,
          ttftMs: channel.ttftMs,
          pingMs: channel.pingMs,
          probeLogs: channel.probeLogs,
          timeline: channel.timeline,
          usageTimeline: channel.usageTimeline,
          probeTimeline: channel.probeTimeline,
        })),
      updatedAt: new Date().toISOString(),
    };
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = adminToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`/v1/admin/dashboard/groups/${encodeURIComponent(groupId)}/timelines`, {
    headers,
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(response.status === 401 ? 'unauthorized' : `dashboard timelines ${response.status}`);
  }
  return (await response.json()) as DashboardGroupTimelines;
}

export function saveAdminToken(token: string): void {
  const normalized = token.trim();
  if (normalized) {
    globalThis.localStorage?.setItem('subpilot_admin_token', normalized);
  }
}

export function clearAdminToken(): void {
  globalThis.localStorage?.removeItem('subpilot_admin_token');
}

export async function fetchDispatchRecords(options: { groupId?: string; hours?: number; signal?: AbortSignal; useMock?: boolean } = {}): Promise<DispatchRecordReport> {
  if (options.useMock || mockFallbackEnabled()) return mockDispatchRecordReport(options.groupId ?? 'all', options.hours ?? 1);
  const params = new URLSearchParams();
  params.set('group_id', options.groupId ?? 'all');
  params.set('hours', String(options.hours ?? 1));
  const response = await fetch(`/v1/admin/dispatch-records?${params.toString()}`, {
    headers: {
      ...authHeaders(),
      Accept: 'application/json',
    },
    signal: options.signal,
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `dispatch records ${response.status}`));
  return (await response.json()) as DispatchRecordReport;
}

export interface ChannelConfigInput {
  enabled: boolean;
  manualPriority: number;
  maxConcurrency: number;
  costCnyPerOfficialUsd: number;
  probeIntervalSeconds: number;
  probeModel: string;
  probePrompt: string;
  probeAsClaudeCode: boolean;
}

export interface GroupConfigInput {
  enabled: boolean;
  forcedAccountId: string;
  forcedFallback: 'smart' | 'reject' | 'strict';
  stickyTtlMs: number;
  maxAcceptableTtftMs: number;
  probeIntervalSeconds: number;
  dispatchStrategy: DispatchStrategy;
  includeNonStreamUsage?: boolean;
}

export async function saveChannelConfig(accountId: string, config: ChannelConfigInput): Promise<void> {
  await putJSON(`/v1/admin/channels/${encodeURIComponent(accountId)}/config`, {
    enabled: config.enabled,
    manual_priority: config.manualPriority,
    max_concurrency: config.maxConcurrency,
    cost_cny_per_official_usd: config.costCnyPerOfficialUsd,
    probe_interval_seconds: config.probeIntervalSeconds,
    probe_model: config.probeModel,
    probe_prompt: config.probePrompt,
    probe_as_claude_code: config.probeAsClaudeCode,
  });
}

interface GroupConfigResponse {
  group_id: string;
  enabled: boolean;
  forced_account_id?: string;
  forced_fallback?: string;
}

export async function setChannelScheduling(accountId: string, enabled: boolean): Promise<void> {
  await putJSON(`/v1/admin/channels/${encodeURIComponent(accountId)}/scheduling`, { enabled });
}

export async function saveGroupConfig(groupId: string, config: GroupConfigInput, verify = false): Promise<void> {
  const path = `/v1/admin/groups/${encodeURIComponent(groupId)}/config`;
  await putJSONWithResponse<GroupConfigResponse>(path, {
    enabled: config.enabled,
    forced_account_id: config.forcedAccountId,
    forced_fallback: config.forcedFallback,
    sticky_ttl_ms: config.stickyTtlMs,
    max_acceptable_ttft_ms: config.maxAcceptableTtftMs,
    probe_interval_seconds: config.probeIntervalSeconds,
    dispatch_strategy: config.dispatchStrategy,
    include_non_stream_usage: config.includeNonStreamUsage ?? true,
  });
  if (!verify) return;
  const response = await fetch(path, { headers: authHeaders() });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `verify ${response.status}`));
  const saved = await response.json() as GroupConfigResponse;
  if (saved.group_id !== groupId
    || saved.enabled !== config.enabled
    || (saved.forced_account_id ?? '') !== config.forcedAccountId
    || (saved.forced_fallback ?? 'smart') !== config.forcedFallback) {
    throw new Error('保存结果校验失败，请刷新后重试');
  }
}

export async function saveRuntimeConfig(config: RuntimeConfig): Promise<void> {
  await putJSON('/v1/admin/runtime/config', config);
}

export async function fetchSystemDiagnostics(options: { signal?: AbortSignal } = {}): Promise<SystemDiagnostics> {
  try {
    const response = await fetch('/v1/admin/system/diagnostics', {
      headers: authHeaders(),
      signal: options.signal,
    });
    if (!response.ok) throw new Error(await responseErrorMessage(response, `system diagnostics ${response.status}`));
    return (await response.json()) as SystemDiagnostics;
  } catch (error) {
    if (options.signal?.aborted || !mockFallbackEnabled()) throw error;
    const now = Math.floor(Date.now() / 1000);
    return {
      lastBalanceSyncAt: now - 180,
      lastCostSyncAt: now - 420,
      lastProbeAt: now - 48,
      latestFailure: '',
      tasks: [
        { task: 'balance_sync', label: '余额同步', lastRunAt: now - 180, success: true, message: '同步完成' },
        { task: 'cost_sync', label: '倍率同步', lastRunAt: now - 420, success: true, message: '同步完成' },
        { task: 'probe', label: '自动探测', lastRunAt: now - 48, success: true, message: '探测完成' },
      ],
      storage: [
        { category: 'events', label: '真实请求事件', bytes: 18_874_368, rows: 128_420 },
        { category: 'probes', label: '自动探测记录', bytes: 4_194_304, rows: 9_860 },
        { category: 'costs', label: '成本同步记录', bytes: 1_048_576, rows: 1_120 },
        { category: 'balances', label: '余额与配置', bytes: 524_288, rows: 84 },
      ],
    };
  }
}

export async function fetchChannelBalanceOverview(options: { signal?: AbortSignal } = {}): Promise<ChannelBalanceOverview> {
  try {
    const response = await fetch('/v1/admin/channel-balances/overview', {
      headers: {
        ...authHeaders(),
        Accept: 'application/json',
      },
      signal: options.signal,
    });
    if (!response.ok) throw new Error(`channel balances ${response.status}`);
    return (await response.json()) as ChannelBalanceOverview;
  } catch (error) {
    if (options.signal?.aborted || !mockFallbackEnabled()) throw error;
    return mockChannelBalanceOverview();
  }
}

export async function saveChannelBalanceSettings(settings: ChannelBalanceSettings): Promise<ChannelBalanceSettings> {
  return putJSONWithResponse<ChannelBalanceSettings>('/v1/admin/channel-balances/settings', settings, 'PUT');
}

export async function createChannelBalanceAccount(payload: ChannelBalanceAccountPayload): Promise<ChannelBalanceAccount> {
  return putJSONWithResponse<ChannelBalanceAccount>('/v1/admin/channel-balances/accounts', payload, 'POST');
}

export async function updateChannelBalanceAccount(id: number, payload: ChannelBalanceAccountPayload): Promise<ChannelBalanceAccount> {
  return putJSONWithResponse<ChannelBalanceAccount>(`/v1/admin/channel-balances/accounts/${encodeURIComponent(id)}`, payload, 'PUT');
}

export async function deleteChannelBalanceAccount(id: number): Promise<void> {
  const response = await fetch(`/v1/admin/channel-balances/accounts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(`delete channel balance ${response.status}`);
}

export async function refreshChannelBalanceAccount(id: number): Promise<ChannelBalanceRefreshResult> {
  return postJSONWithResponse<ChannelBalanceRefreshResult>(`/v1/admin/channel-balances/accounts/${encodeURIComponent(id)}/refresh`);
}

export async function refreshAllChannelBalances(): Promise<ChannelBalanceRefreshResult[]> {
  return postJSONWithResponse<ChannelBalanceRefreshResult[]>('/v1/admin/channel-balances/accounts/refresh');
}

export async function saveUpstreamCostSyncSettings(settings: UpstreamCostSyncSettings): Promise<UpstreamCostSyncSettings> {
  return putJSONWithResponse<UpstreamCostSyncSettings>('/v1/admin/upstream-cost-sync/settings', settings, 'PUT');
}

export async function runUpstreamCostSync(balanceAccountId?: number): Promise<UpstreamCostSyncResult[]> {
  const query = balanceAccountId ? `?balance_account_id=${encodeURIComponent(balanceAccountId)}` : '';
  return postJSONWithResponse<UpstreamCostSyncResult[]>(`/v1/admin/upstream-cost-sync/run${query}`);
}

export async function fetchCostManagementOverview(options: { signal?: AbortSignal } = {}): Promise<CostManagementOverview> {
  try {
    const response = await fetch('/v1/admin/cost-management/overview', {
      headers: {
        ...authHeaders(),
        Accept: 'application/json',
      },
      signal: options.signal,
    });
    if (!response.ok) throw new Error(await responseErrorMessage(response, `cost management ${response.status}`));
    return (await response.json()) as CostManagementOverview;
  } catch (error) {
    if (options.signal?.aborted || !mockFallbackEnabled()) throw error;
    return mockCostManagementOverview();
  }
}

export async function saveCostManagementSettings(settings: CostManagementSettings): Promise<CostManagementSettings> {
  return putJSONWithResponse<CostManagementSettings>('/v1/admin/cost-management/settings', settings, 'PUT');
}

export async function fetchCostManagementHistory(options: { balanceAccountId?: number; accountId?: string; limit?: number; signal?: AbortSignal } = {}): Promise<CostManagementHistory[]> {
  const params = new URLSearchParams();
  if (options.balanceAccountId) params.set('balance_account_id', String(options.balanceAccountId));
  if (options.accountId) params.set('account_id', options.accountId);
  if (options.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  const response = await fetch(`/v1/admin/cost-management/history${query ? `?${query}` : ''}`, {
    headers: {
      ...authHeaders(),
      Accept: 'application/json',
    },
    signal: options.signal,
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `cost history ${response.status}`));
  return (await response.json()) as CostManagementHistory[];
}

export async function fetchDailyProfitOverview(options: { date?: string; signal?: AbortSignal } = {}): Promise<DailyProfitOverview> {
  try {
    const params = new URLSearchParams();
    if (options.date) params.set('date', options.date);
    const query = params.toString();
    const response = await fetch(`/v1/admin/daily-profit/overview${query ? `?${query}` : ''}`, {
      headers: {
        ...authHeaders(),
        Accept: 'application/json',
      },
      signal: options.signal,
    });
    if (!response.ok) throw new Error(await responseErrorMessage(response, `daily profit ${response.status}`));
    return (await response.json()) as DailyProfitOverview;
  } catch (error) {
    if (options.signal?.aborted || !mockFallbackEnabled()) throw error;
    return mockDailyProfitOverview(options.date);
  }
}

export async function fetchDailyProfitTrend(options: { date?: string; days?: number; signal?: AbortSignal } = {}): Promise<DailyProfitTrend> {
  try {
    const params = new URLSearchParams();
    if (options.date) params.set('date', options.date);
    if (options.days) params.set('days', String(options.days));
    const query = params.toString();
    const response = await fetch(`/v1/admin/daily-profit/trend${query ? `?${query}` : ''}`, {
      headers: {
        ...authHeaders(),
        Accept: 'application/json',
      },
      signal: options.signal,
    });
    if (!response.ok) throw new Error(await responseErrorMessage(response, `daily profit trend ${response.status}`));
    return (await response.json()) as DailyProfitTrend;
  } catch (error) {
    if (options.signal?.aborted || !mockFallbackEnabled()) throw error;
    return mockDailyProfitTrend(options.date, options.days ?? 7);
  }
}

export async function fetchNotificationConfig(options: { signal?: AbortSignal } = {}): Promise<NotificationConfig> {
  const response = await fetch('/v1/admin/notification/config', {
    headers: {
      ...authHeaders(),
      Accept: 'application/json',
    },
    signal: options.signal,
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `notification config ${response.status}`));
  return (await response.json()) as NotificationConfig;
}

export async function saveNotificationConfig(config: NotificationConfig): Promise<NotificationConfig> {
  return putJSONWithResponse<NotificationConfig>('/v1/admin/notification/config', config, 'PUT');
}

export async function testNotification(): Promise<NotificationTestResult> {
  return postJSONWithResponse<NotificationTestResult>('/v1/admin/notification/test');
}

async function putJSON(path: string, body: unknown): Promise<void> {
  const response = await fetch(path, {
    method: 'PUT',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `save ${response.status}`));
}

async function putJSONWithResponse<T>(path: string, body: unknown, method = 'PUT'): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `save ${response.status}`));
  return (await response.json()) as T;
}

async function postJSONWithResponse<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response, `refresh ${response.status}`));
  return (await response.json()) as T;
}

async function responseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json() as { error?: string; message?: string };
    return body.error || body.message || fallback;
  } catch {
    try {
      const text = await response.text();
      return text.trim() || fallback;
    } catch {
      return fallback;
    }
  }
}

function adminToken(): string {
  const stored = globalThis.localStorage?.getItem('subpilot_admin_token')?.trim();
  if (stored) return stored;
  return import.meta.env.VITE_SUBPILOT_ADMIN_TOKEN?.trim() ?? '';
}

function authHeaders(): Record<string, string> {
  const token = adminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function mockFallbackEnabled(): boolean {
  return import.meta.env.VITE_SUBPILOT_MOCK_FALLBACK === 'true';
}

export function filterChannels(channels: Channel[], filters: ChannelFilters): Channel[] {
  const keyword = filters.search.trim().toLowerCase();

  return channels.filter((channel) => {
    const groupMatches = filters.groupId === 'all' || channel.group.id === filters.groupId;
    const providerMatches = filters.provider === 'all' || channel.provider === filters.provider;
    const keywordMatches =
      keyword.length === 0 ||
      [channel.name, channel.model, channel.probeModel, channel.providerLabel, channel.group.name]
        .some((field) => field.toLowerCase().includes(keyword));

    return groupMatches && providerMatches && keywordMatches;
  }).sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    if (a.isDispatching !== b.isDispatching) return a.isDispatching ? -1 : 1;
    const healthRank = (channel: Channel) => channel.dispatchHealthGrade === 'healthy' ? 0 : channel.dispatchHealthGrade === 'degraded' ? 1 : 2;
    const rankDifference = healthRank(a) - healthRank(b);
    if (rankDifference !== 0) return rankDifference;
    const scoreDifference = (b.dispatchScore?.total ?? b.score) - (a.dispatchScore?.total ?? a.score);
    if (scoreDifference !== 0) return scoreDifference;
    return b.availability7d - a.availability7d;
  });
}

export function sortChannels(channels: Channel[], mode: ChannelSortMode): Channel[] {
  if (mode === 'recommended') return [...channels];

  return channels
    .map((channel, index) => ({ channel, index }))
    .sort((left, right) => {
      const a = left.channel;
      const b = right.channel;
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;

      let difference = 0;
      switch (mode) {
        case 'abnormal_first':
          difference = dispatchHealthRank(b) - dispatchHealthRank(a);
          if (difference === 0) difference = recentHealthRate(a) - recentHealthRate(b);
          if (difference === 0) difference = recentSlowRate(b) - recentSlowRate(a);
          break;
        case 'cost_asc':
        case 'cost_desc': {
          const aCost = validChannelCost(a);
          const bCost = validChannelCost(b);
          if (aCost === null || bCost === null) {
            if (aCost !== bCost) difference = aCost === null ? 1 : -1;
          } else {
            difference = mode === 'cost_asc' ? aCost - bCost : bCost - aCost;
          }
          break;
        }
        case 'health_desc': {
          difference = dispatchHealthRank(a) - dispatchHealthRank(b);
          if (difference === 0) difference = recentHealthRate(b) - recentHealthRate(a);
          if (difference === 0) difference = recentSlowRate(a) - recentSlowRate(b);
          break;
        }
        case 'score_desc':
          difference = channelDispatchScore(b) - channelDispatchScore(a);
          break;
        case 'ttft_asc': {
          const aTTFT = validChannelTTFT(a);
          const bTTFT = validChannelTTFT(b);
          if (aTTFT === null || bTTFT === null) {
            if (aTTFT !== bTTFT) difference = aTTFT === null ? 1 : -1;
          } else {
            difference = aTTFT - bTTFT;
          }
          break;
        }
      }

      if (difference !== 0) return difference;
      return left.index - right.index;
    })
    .map(({ channel }) => channel);
}

function validChannelCost(channel: Channel): number | null {
  return Number.isFinite(channel.costCnyPerUsd) && channel.costCnyPerUsd > 0 ? channel.costCnyPerUsd : null;
}

function validChannelTTFT(channel: Channel): number | null {
  return channel.ttftMs !== null && Number.isFinite(channel.ttftMs) && channel.ttftMs >= 0 ? channel.ttftMs : null;
}

function dispatchHealthRank(channel: Channel): number {
  return channel.dispatchHealthGrade === 'healthy' ? 0 : channel.dispatchHealthGrade === 'degraded' ? 1 : 2;
}

function recentHealthWindow(channel: Channel): HealthWindow | undefined {
  return ['5m', '30m', '1h']
    .map((window) => channel.healthWindows?.find((item) => item.window === window && item.requests > 0))
    .find(Boolean);
}

function recentHealthRate(channel: Channel): number {
  return recentHealthWindow(channel)?.successRate ?? channel.availability7d;
}

function recentSlowRate(channel: Channel): number {
  return recentHealthWindow(channel)?.slowRate ?? 101;
}

function channelDispatchScore(channel: Channel): number {
  return channel.dispatchScore?.total ?? channel.score;
}

const statuses: Array<TimelinePoint['status']> = ['healthy', 'healthy', 'healthy', 'idle', 'degraded', 'failed'];

function makeTimeline(seed: number): TimelinePoint[] {
  return Array.from({ length: 60 }, (_, index) => {
    const status = index < seed ? 'failed' : statuses[(index + seed) % statuses.length];
    return {
      status,
      ttftMs: status === 'failed' || status === 'idle' ? null : 640 + ((index * 73 + seed * 29) % 1700),
      pingMs: status === 'failed' || status === 'idle' ? null : 18 + ((index * 11 + seed) % 90),
      checkedAt: new Date(Date.now() - (59 - index) * 60_000).toISOString(),
    };
  });
}

const groups: GroupSummary[] = [
  { id: 'plus', name: 'GPT PLUS', mode: 'smart', probeIntervalSec: 60, maxAcceptableTtftMs: 2500, dispatchStrategy: 'stable_first', officialUsdToday: 2, spendCnyToday: 1.16, compositeCostToday: 0.58, officialUsd24h: 5, spendCny24h: 3.06, compositeCost24h: 0.612 },
  { id: 'claude', name: 'Claude Pro', mode: 'smart', probeIntervalSec: 120, maxAcceptableTtftMs: 3000, dispatchStrategy: 'stable_first', officialUsdToday: 1.8, spendCnyToday: 1.242, compositeCostToday: 0.69, officialUsd24h: 4.7, spendCny24h: 3.31, compositeCost24h: 0.7043 },
  { id: 'batch', name: '低价批处理', mode: 'forced', forcedChannelId: 'or-01', probeIntervalSec: 180, maxAcceptableTtftMs: 5000, dispatchStrategy: 'cost_first', officialUsdToday: 0.4, spendCnyToday: 0.16, compositeCostToday: 0.4, officialUsd24h: 1.2, spendCny24h: 0.4944, compositeCost24h: 0.412 },
];

function channel(overrides: Partial<Channel> & Pick<Channel, 'id' | 'name' | 'provider' | 'providerLabel' | 'model' | 'group' | 'status' | 'ttftMs' | 'pingMs' | 'availability7d'>): Channel {
  const failed = overrides.status === 'failed';
  const degraded = overrides.status === 'degraded' || overrides.status === 'cooldown';
  const score = failed ? 412 : degraded ? 684 : 913;

  return {
    enabled: overrides.enabled ?? true,
    probeModel: overrides.provider === 'anthropic' ? 'claude-3-haiku-20240307' : 'gpt-5.4-mini',
    probePrompt: '',
    probeAsClaudeCode: false,
    statusText: failed ? '异常' : degraded ? '降级' : '正常',
    dispatchHealthGrade: failed ? 'unhealthy' : degraded ? 'degraded' : 'healthy',
    dispatchHealthText: failed ? '健康异常' : degraded ? '健康降级' : '健康正常',
    dispatchHealthReasons: failed
      ? ['近期失败率偏高']
      : degraded
        ? ['近期慢请求率偏高']
        : ['符合调度健康阈值'],
    costCnyPerUsd: 0.7652,
    manualPriority: 80,
    forced: false,
    requestToday: 423,
    officialUsdToday: 9.75,
    availabilityToday: failed ? 71.25 : degraded ? 91.5 : 99.25,
    compositeCostToday: 0.7421,
    request24h: 1842,
    officialUsd24h: 38.42,
    cacheTokens24h: 124_880,
    compositeCost24h: 0.7652,
    score,
    isDispatching: false,
    dispatchScore: {
      total: score + 56,
      components: {
        stability: failed ? 180 : degraded ? 540 : 930,
        latency: failed ? 0 : degraded ? 16 : 42,
        cacheAffinity: 0,
        nativePriority: -1,
        manualPriority: overrides.manualPriority ?? 80,
        costPenalty: -(overrides.costCnyPerUsd ? Math.round(overrides.costCnyPerUsd * 1000) / 6 : 61),
        switchingPenalty: 0,
        inflightPenalty: -12,
        healthPenalty: failed ? -150 : degraded ? -150 : 0,
      },
    },
    healthWindows: [
      { window: '5m', requests: failed ? 12 : degraded ? 16 : 18, failures: failed ? 9 : degraded ? 4 : 0, successRate: failed ? 25 : degraded ? 75 : 100, slowRate: failed ? 58.33 : degraded ? 43.75 : 5.56, severeSlowCount: failed ? 4 : degraded ? 2 : 0 },
      { window: '30m', requests: failed ? 68 : degraded ? 96 : 112, failures: failed ? 26 : degraded ? 15 : 3, successRate: failed ? 61.76 : degraded ? 84.38 : 97.32, slowRate: failed ? 36.76 : degraded ? 28.13 : 8.93, severeSlowCount: failed ? 9 : degraded ? 6 : 1 },
      { window: '1h', requests: failed ? 120 : degraded ? 180 : 220, failures: failed ? 42 : degraded ? 25 : 7, successRate: failed ? 65 : degraded ? 86.11 : 96.82, slowRate: failed ? 31.67 : degraded ? 24.44 : 7.27, severeSlowCount: failed ? 14 : degraded ? 9 : 2 },
      { window: '24h', requests: overrides.request24h ?? 1842, failures: Math.max(0, Math.round((overrides.request24h ?? 1842) * (100 - overrides.availability7d) / 100)), successRate: overrides.availability7d, slowRate: failed ? 18.9 : degraded ? 14.45 : 3.7, severeSlowCount: failed ? 31 : degraded ? 18 : 3 },
    ],
    scoreParts: [
      { label: '稳定性', value: failed ? 280 : degraded ? 620 : 930, max: 1000, tone: failed ? 'bad' : degraded ? 'warn' : 'good' },
      { label: 'TTFT', value: failed ? 20 : degraded ? 132 : 210, max: 240, tone: degraded ? 'warn' : 'good' },
      { label: '缓存亲和', value: 168, max: 220, tone: 'good' },
      { label: '手动优先级', value: overrides.manualPriority ?? 80, max: 200, tone: 'neutral' },
      { label: '成本扣分', value: -(overrides.costCnyPerUsd ? Math.round(overrides.costCnyPerUsd * 80) : 61), max: 180, tone: 'warn' },
    ],
    errorTrend: {
      auth_error: failed ? 2 : 0,
      quota_exhausted: 0,
      rate_limited: degraded ? 8 : 2,
      overloaded: degraded ? 12 : 3,
      timeout: failed ? 7 : 1,
      network_error: degraded ? 3 : 1,
    },
    errorDetails: [
      {
        statusCode: 502,
        errorType: 'server_error',
        errorMessage: 'Upstream service temporarily unavailable',
        count30m: 7,
        lastSeenAt: '2026-06-19T20:14:23Z',
      },
    ],
    requestTrend: [
      { label: '00', success: 84, failed: 2 },
      { label: '04', success: 63, failed: failed ? 18 : 1 },
      { label: '08', success: 126, failed: degraded ? 11 : 3 },
      { label: '12', success: 182, failed: 4 },
      { label: '16', success: 207, failed: 6 },
      { label: '20', success: 154, failed: failed ? 22 : 3 },
    ],
    probeLogs: [
      { time: '48s 前', status: overrides.status, model: overrides.model, latencyMs: overrides.ttftMs, message: failed ? 'timeout after 8s' : 'probe ok' },
      { time: '6m 前', status: 'healthy', model: overrides.model, latencyMs: 812, message: 'probe ok' },
      { time: '16m 前', status: degraded ? 'degraded' : 'healthy', model: overrides.model, latencyMs: 1340, message: degraded ? 'upstream overloaded' : 'probe ok' },
    ],
    timeline: makeTimeline(failed ? 18 : degraded ? 7 : 2),
    usageTimeline: makeTimeline(failed ? 8 : degraded ? 3 : 1),
    probeTimeline: makeTimeline(failed ? 20 : degraded ? 9 : 2),
    ...overrides,
  };
}

export const mockDashboard: DashboardData = {
  groups,
  providers: ['openai', 'anthropic', 'gemini', 'openrouter'],
  runtime: {
    dispatchEnabled: false,
    dispatchOAuthPreference: 'prefer_healthy',
    dispatchFailOpen: true,
    dispatchSelectTimeoutMs: 80,
    dispatchAutoBypassFailures: 3,
    dispatchAutoRecover: true,
    probeEnabled: false,
    probeIntervalSeconds: 60,
    probeImageIntervalSeconds: 1200,
    probeJitterSeconds: 0,
    probeMaxConcurrentAccounts: 8,
    probeTimeoutSeconds: 30,
    probeRetryCount: 1,
    alertMergeWindowMinutes: 30,
    dataRetentionDays: 7,
  },
  recentDecisions: [
    {
      requestId: 'mock-req-1',
      groupId: 'plus',
      platform: 'openai',
      model: 'gpt-5.5',
      decision: 'selected',
      reason: 'cost_first',
      reasonText: '成本优先：满足健康阈值后选择低成本账号',
      selectedAccountId: 'openai-fast',
      createdAt: new Date().toISOString(),
      skippedByReason: {},
      scores: [
        {
          AccountID: 'openai-008',
          accountName: 'GPT PLUS 0.08（上链接）',
          costCnyPerUsd: 0.7652,
          Total: 969,
          Components: { Stability: 930, Latency: 42, CacheAffinity: 0, NativePriority: -1, ManualPriority: 120, CostPenalty: -127.5, SwitchingPenalty: 0, InflightPenalty: -12, HealthPenalty: 0 },
        },
        {
          AccountID: 'openai-fast',
          accountName: 'GPT PLUS Fast Lane',
          costCnyPerUsd: 0.682,
          Total: 944,
          Components: { Stability: 900, Latency: 16, CacheAffinity: 0, NativePriority: -1, ManualPriority: 95, CostPenalty: -113.67, SwitchingPenalty: 0, InflightPenalty: -12, HealthPenalty: 0 },
        },
      ],
    },
  ],
  updatedAt: new Date().toISOString(),
  channels: [
    channel({
      id: 'openai-008',
      name: 'GPT PLUS 0.08（上链接）',
      provider: 'openai',
      providerLabel: 'OpenAI',
      model: 'gpt-5.5',
      group: groups[0],
      status: 'healthy',
      ttftMs: 1616,
      pingMs: 0,
      availability7d: 94.54,
      costCnyPerUsd: 0.7652,
      manualPriority: 120,
      isDispatching: false,
    }),
    channel({
      id: 'openai-fast',
      name: 'GPT PLUS Fast Lane',
      provider: 'openai',
      providerLabel: 'OpenAI',
      model: 'gpt-5.4-mini',
      group: groups[0],
      status: 'degraded',
      ttftMs: 2380,
      pingMs: 42,
      availability7d: 88.12,
      costCnyPerUsd: 0.682,
      manualPriority: 95,
      isDispatching: true,
    }),
    channel({
      id: 'claude-main',
      name: 'Claude 主账号 A',
      provider: 'anthropic',
      providerLabel: 'Anthropic',
      model: 'claude-sonnet-4',
      group: groups[1],
      status: 'healthy',
      ttftMs: 982,
      pingMs: 35,
      availability7d: 97.48,
      costCnyPerUsd: 0.812,
      manualPriority: 105,
    }),
    channel({
      id: 'gemini-cache',
      name: 'Gemini 缓存池 02',
      provider: 'gemini',
      providerLabel: 'Gemini',
      model: 'gemini-2.5-pro',
      group: groups[1],
      status: 'cooldown',
      ttftMs: 3120,
      pingMs: 84,
      availability7d: 82.93,
      costCnyPerUsd: 0.594,
      manualPriority: 60,
    }),
    channel({
      id: 'or-01',
      name: 'OpenRouter 批处理 01',
      provider: 'openrouter',
      providerLabel: 'OpenRouter',
      model: 'gpt-4o-mini',
      group: groups[2],
      status: 'failed',
      ttftMs: null,
      pingMs: null,
      availability7d: 63.4,
      costCnyPerUsd: 0.412,
      manualPriority: 30,
      forced: true,
    }),
  ],
};

function mockDispatchRecordReport(groupId: string, hours: number): DispatchRecordReport {
  const allRecords: RecentDecision[] = [
    {
      requestId: 'mock-dispatch-3',
      groupId: 'plus',
      groupName: 'GPT PLUS',
      platform: 'openai',
      model: 'gpt-5.5',
      decision: 'selected',
      reason: 'cost_first',
      reasonText: '成本优先：满足健康阈值后选择低成本账号',
      selectedAccountId: 'openai-008',
      selectedAccountName: 'GPT PLUS 0.08（上链接）',
      previousAccountId: 'openai-fast',
      previousAccountName: 'GPT PLUS Fast Lane',
      selectedCostCnyPerUsd: 0.0765,
      previousCostCnyPerUsd: 0.088,
      costDeltaCnyPerUsd: -0.0115,
      changeType: 'switch',
      selectedScore: 969,
      previousScore: 948,
      createdAt: new Date(Date.now() - 6 * 60_000).toISOString(),
      skippedByReason: {},
      scores: [],
    },
    {
      requestId: 'mock-dispatch-2',
      groupId: 'claude',
      groupName: 'Claude Pro',
      platform: 'anthropic',
      model: 'claude-sonnet-4-6',
      decision: 'selected',
      reason: 'sticky',
      reasonText: '缓存亲和：沿用当前会话账号',
      selectedAccountId: 'claude-main',
      selectedAccountName: 'Claude 主账号 A',
      previousAccountId: 'claude-main',
      previousAccountName: 'Claude 主账号 A',
      selectedCostCnyPerUsd: 0.0812,
      previousCostCnyPerUsd: 0.0812,
      costDeltaCnyPerUsd: 0,
      changeType: 'same',
      selectedScore: 1004,
      previousScore: 1004,
      createdAt: new Date(Date.now() - 26 * 60_000).toISOString(),
      skippedByReason: {},
      scores: [],
    },
    {
      requestId: 'mock-dispatch-1',
      groupId: 'plus',
      groupName: 'GPT PLUS',
      platform: 'openai',
      model: 'gpt-5.5',
      decision: 'selected',
      reason: 'fallback_degraded',
      reasonText: '兜底：健康账号不足，选择降级账号',
      selectedAccountId: 'openai-fast',
      selectedAccountName: 'GPT PLUS Fast Lane',
      previousAccountId: 'openai-008',
      previousAccountName: 'GPT PLUS 0.08（上链接）',
      selectedCostCnyPerUsd: 0.088,
      previousCostCnyPerUsd: 0.0765,
      costDeltaCnyPerUsd: 0.0115,
      changeType: 'switch',
      selectedScore: 812,
      previousScore: 642,
      createdAt: new Date(Date.now() - 47 * 60_000).toISOString(),
      skippedByReason: { cooldown: 1 },
      scores: [],
    },
  ];
  const records = allRecords.filter((record) => groupId === 'all' || record.groupId === groupId);

  const totalCost = records.reduce((sum, record) => sum + (record.selectedCostCnyPerUsd ?? 0), 0);
  const estimatedCostDelta = records.reduce((sum, record) => sum + (record.costDeltaCnyPerUsd ?? 0), 0);
  return {
    groupId,
    rangeHours: hours,
    generatedAt: new Date().toISOString(),
    summary: {
      totalDecisions: records.length,
      switches: records.filter((record) => record.changeType === 'switch').length,
      costDownSwitches: records.filter((record) => (record.costDeltaCnyPerUsd ?? 0) < 0).length,
      costUpSwitches: records.filter((record) => (record.costDeltaCnyPerUsd ?? 0) > 0).length,
      noChange: records.filter((record) => record.changeType !== 'switch').length,
      averageSelectedCost: records.length ? totalCost / records.length : 0,
      estimatedCostDelta,
      topReasons: [
        { reason: 'cost_first', reasonText: '成本优先', count: 1 },
        { reason: 'sticky', reasonText: '缓存亲和', count: 1 },
        { reason: 'fallback_degraded', reasonText: '降级兜底', count: 1 },
      ],
    },
    records,
  };
}

function mockChannelBalanceOverview(): ChannelBalanceOverview {
  const now = Math.floor(Date.now() / 1000);
  const items: ChannelBalanceAccount[] = [
    {
      id: 1,
      name: 'P神 主账户',
      type: 'sub2api',
      base_url: 'http://43.153.188.132:6689',
      recharge_url: 'https://pay.example.com/pshen',
      upstream_user_id: 0,
      enabled: true,
      balance: 842.65,
      balance_exchange_rate: 1,
      converted_balance: 842.65,
      unit: '元',
      balance_updated_time: now - 420,
      threshold: 100,
      alert_enabled: true,
      alert_cooldown_hours: 24,
      last_alert_time: 0,
      contact_channel: 'wechat',
      contact_info: 'pshen-support',
      remark: 'GPT PLUS / Claude Pro 共用',
      billing_identity: 'pshen-main',
      sub2api_email: 'admin@example.com',
      recharge_multiplier: 1,
      cost_sync_enabled: true,
      cost_sync_locked: false,
      cost_sync_last_time: now - 800,
      cost_sync_last_success: true,
      cost_sync_last_message: '倍率同步成功',
      created_at: new Date((now - 86400 * 12) * 1000).toISOString(),
      updated_at: new Date((now - 420) * 1000).toISOString(),
      has_key: true,
      has_sub2api_password: true,
      low_balance: false,
    },
    {
      id: 2,
      name: 'codelife 备用池',
      type: 'newapi',
      base_url: 'https://api.codelife.eu.cc',
      recharge_url: 'https://pay.example.com/codelife',
      upstream_user_id: 7,
      enabled: true,
      balance: 63.2,
      balance_exchange_rate: 10,
      converted_balance: 6.32,
      unit: '元',
      balance_updated_time: now - 960,
      threshold: 20,
      alert_enabled: true,
      alert_cooldown_hours: 12,
      last_alert_time: now - 3600,
      contact_channel: 'telegram',
      contact_info: '@codelife_support',
      remark: '1:10 余额口径',
      billing_identity: 'codelife',
      recharge_multiplier: 0.1,
      cost_sync_enabled: true,
      cost_sync_locked: false,
      cost_sync_last_time: now - 960,
      cost_sync_last_success: true,
      cost_sync_last_message: '倍率同步成功',
      created_at: new Date((now - 86400 * 5) * 1000).toISOString(),
      updated_at: new Date((now - 960) * 1000).toISOString(),
      has_key: true,
      has_sub2api_password: false,
      low_balance: true,
    },
  ];
  return {
    summary: {
      total: items.length,
      monitored: items.filter((item) => item.enabled).length,
      low_balance: items.filter((item) => item.low_balance).length,
      alert_enabled: items.filter((item) => item.alert_enabled).length,
      last_refresh_time: now - 420,
    },
    settings: {
      auto_refresh_minutes: 0,
      cost_sync_interval_minutes: 15,
    },
    items,
  };
}

function mockCostManagementOverview(): CostManagementOverview {
  const now = Math.floor(Date.now() / 1000);
  const accounts: CostManagementAccount[] = [
    {
      balance_account_id: 1,
      balance_account_name: 'P神',
      upstream_type: 'sub2api',
      base_url: 'http://43.153.188.132:6689',
      account_id: 'openai-fast',
      account_name: 'P神',
      api_key_fingerprint: 'sk-...p6',
      group_id: 'plus',
      group_name: 'plus',
      normal_multiplier: 0.13,
      dedicated_multiplier: 0.1,
      effective_multiplier: 0.1,
      multiplier_source: 'dedicated',
      recharge_multiplier: 1,
      calculated_cost: 0.1,
      previous_cost: 0.1,
      current_config_cost: 0.1,
      change_percent: 0,
      status: 'ok',
      message: '成本无需更新',
      locked: false,
      last_sync_time: now - 3600,
    },
    {
      balance_account_id: 1,
      balance_account_name: 'P神',
      upstream_type: 'sub2api',
      base_url: 'http://43.153.188.132:6689',
      account_id: 'claude-main',
      account_name: 'P神 Claude',
      api_key_fingerprint: 'sk-...p8',
      group_id: 'claude',
      group_name: 'claude-pro',
      normal_multiplier: 0.16,
      dedicated_multiplier: undefined,
      effective_multiplier: 0.16,
      multiplier_source: 'normal',
      recharge_multiplier: 1,
      calculated_cost: 0.16,
      previous_cost: 0.12,
      current_config_cost: 0.12,
      change_percent: 33.33,
      status: 'cost_increased',
      message: '计算成本高于配置成本',
      locked: false,
      last_sync_time: now - 3600,
    },
    {
      balance_account_id: 2,
      balance_account_name: 'codelife',
      upstream_type: 'newapi',
      base_url: 'https://api.codelife.eu.cc',
      account_id: 'or-01',
      account_name: 'codelife',
      api_key_fingerprint: 'sk-...cl',
      group_id: 'cc-kiro-high-huan',
      group_name: 'cc-kiro-high-huan',
      normal_multiplier: 0.8,
      dedicated_multiplier: undefined,
      effective_multiplier: 0.8,
      multiplier_source: 'normal',
      recharge_multiplier: 0.1,
      calculated_cost: 0.08,
      previous_cost: 0.08,
      current_config_cost: 0.08,
      change_percent: 0,
      status: 'ok',
      message: '成本无需更新',
      locked: false,
      last_sync_time: now - 3600,
    },
  ];

  return {
    settings: {
      interval_minutes: 15,
      alert_enabled: true,
      alert_threshold_percent: 20,
    },
    upstreams: [
      {
        balance_account_id: 1,
        balance_account_name: 'P神',
        upstream_type: 'sub2api',
        base_url: 'http://43.153.188.132:6689',
        recharge_multiplier: 1,
        accounts: accounts.filter((account) => account.balance_account_id === 1),
      },
      {
        balance_account_id: 2,
        balance_account_name: 'codelife',
        upstream_type: 'newapi',
        base_url: 'https://api.codelife.eu.cc',
        recharge_multiplier: 0.1,
        accounts: accounts.filter((account) => account.balance_account_id === 2),
      },
    ],
    recent_changes: [{
      ...accounts[1],
      id: 1,
      success: true,
      notified: true,
      sync_time: now - 3600,
      created_at: new Date((now - 3600) * 1000).toISOString(),
    }],
    updated_at: new Date().toISOString(),
  };
}

function mockDailyProfitOverview(date?: string): DailyProfitOverview {
  const day = date || new Date().toISOString().slice(0, 10);
  const updatedAt = new Date().toISOString();
  return {
    date: day,
    timezone: 'Asia/Shanghai',
    start_at: `${day}T00:00:00+08:00`,
    end_at: `${day}T23:59:59+08:00`,
    complete: false,
    warnings: ['codeapx：upstream status code: 404', 'youc：upstream status code: 400'],
    summary: {
      income: 11.504,
      newapi_income: 0,
      sub2api_direct_income: 11.504,
      upstream_cost: 4.286,
      profit: 7.218,
      profit_rate: 62.75,
      income_requests: 615,
      upstream_requests: 615,
      income_tokens: 44124159,
      upstream_tokens: 44124159,
      successful_cost_sources: 1,
      failed_cost_sources: 2,
    },
    income: {
      newapi: {
        name: 'NewAPI',
        amount: 0,
        requests: 0,
        tokens: 0,
        status: 'unconfigured',
        message: '未配置 NewAPI 收入口径',
      },
      sub2api_direct: {
        name: 'Sub2API 直连下游',
        amount: 11.504,
        requests: 615,
        tokens: 44124159,
        status: 'ok',
        message: '',
      },
    },
    upstream_costs: [
      {
        balance_account_id: 1,
        balance_account_name: 'Sub2API 直连上游',
        upstream_type: 'sub2api',
        base_url: 'http://43.153.188.132:6689',
        recharge_multiplier: 1,
        actual_cost: 4.286,
        standard_cost: 4.286,
        converted_cost: 4.286,
        requests: 482,
        tokens: 32891200,
        status: 'ok',
        message: '',
      },
      {
        balance_account_id: 2,
        balance_account_name: 'codelife',
        upstream_type: 'newapi',
        base_url: 'https://api.codelife.eu.cc',
        recharge_multiplier: 0.1,
        actual_cost: 2.14,
        standard_cost: 2.14,
        converted_cost: 0.214,
        requests: 133,
        tokens: 11232959,
        status: 'duplicate',
        message: '已按同一计费账户合并',
      },
    ],
    updated_at: updatedAt,
  };
}

function mockDailyProfitTrend(date?: string, days = 7): DailyProfitTrend {
  const end = date ? new Date(`${date}T00:00:00`) : new Date();
  const points: DailyProfitTrendPoint[] = Array.from({ length: Math.max(1, days) }).map((_, index) => {
    const day = new Date(end);
    day.setDate(end.getDate() - (days - index - 1));
    const income = 8 + index * 0.72;
    const upstreamCost = 3 + index * 0.31;
    const profit = income - upstreamCost;
    return {
      date: day.toISOString().slice(0, 10),
      income,
      upstream_cost: upstreamCost,
      profit,
      profit_rate: (profit / income) * 100,
      complete: index < days - 1,
    };
  });

  return {
    date: date || end.toISOString().slice(0, 10),
    timezone: 'Asia/Shanghai',
    days,
    points,
    updated_at: new Date().toISOString(),
  };
}
